import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Plus, Clock, CheckCircle, XCircle, Lightbulb, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import SegmentedFilter from "@/components/dashboard/SegmentedFilter";

interface Submission {
  id: string;
  status: string;
  submission_type: string | null;
  text_input: string | null;
  urls: any;
  created_at: string;
  linked_resource_id: string | null;
}

const statusConfig: Record<string, { icon: React.ElementType; variant: "default" | "secondary" | "outline" | "destructive"; label: string }> = {
  pending: { icon: Clock, variant: "secondary", label: "Pending" },
  processing: { icon: Clock, variant: "secondary", label: "Processing" },
  ready_for_review: { icon: Clock, variant: "outline", label: "In Review" },
  approved: { icon: CheckCircle, variant: "default", label: "Approved" },
  rejected: { icon: XCircle, variant: "destructive", label: "Rejected" },
};

const filterOptions = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const UserSubmissions: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: submissions, isLoading } = useQuery({
    queryKey: ["user-submissions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("pending_resources")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Submission[];
    },
  });

  const filteredSubmissions = React.useMemo(() => {
    if (!submissions) return [];
    if (statusFilter === "all") return submissions;
    if (statusFilter === "pending") {
      return submissions.filter((s) => 
        ["pending", "processing", "ready_for_review"].includes(s.status)
      );
    }
    return submissions.filter((s) => s.status === statusFilter);
  }, [submissions, statusFilter]);

  const getSubmissionTitle = (submission: Submission) => {
    if (submission.text_input) {
      return submission.text_input.substring(0, 80) + (submission.text_input.length > 80 ? "..." : "");
    }
    if (submission.urls && Array.isArray(submission.urls) && submission.urls.length > 0) {
      return submission.urls[0];
    }
    return "Resource submission";
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Hero CTA Card */}
      <Card className="mb-6 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 border-primary/20">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-shrink-0 p-3 rounded-full bg-primary/10">
              <Lightbulb className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-lg font-semibold mb-1">
                Know a great resource?
              </h2>
              <p className="text-muted-foreground text-sm">
                Share it with the community! Submit articles, tools, videos, or any helpful content.
              </p>
            </div>
            <Link to="/library">
              <Button size="lg">
                <Plus className="h-4 w-4 mr-2" />
                Submit Resource
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Header with Filter */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
            <Send className="h-7 w-7 text-primary" />
            My Submissions
          </h1>
          <p className="text-muted-foreground mt-1">
            Track the status of resources you've submitted.
          </p>
        </div>
        <SegmentedFilter
          options={filterOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : !filteredSubmissions.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Send className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {statusFilter !== "all"
                ? `No ${statusFilter} submissions.`
                : "No submissions yet."}
            </p>
            {statusFilter === "all" && (
              <p className="text-sm text-muted-foreground mt-2">
                Submit resources to contribute to the library.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSubmissions.map((submission) => {
            const status = statusConfig[submission.status] || statusConfig.pending;
            const StatusIcon = status.icon;

            return (
              <Card key={submission.id} className="group">
                <CardHeader className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={status.variant} className="shrink-0">
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </Badge>
                        {submission.submission_type && (
                          <span className="text-xs text-muted-foreground capitalize">
                            {submission.submission_type.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-base font-medium line-clamp-2">
                        {getSubmissionTitle(submission)}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Submitted {format(new Date(submission.created_at), "PPp")}
                      </p>
                    </div>
                    {submission.status === "approved" && submission.linked_resource_id && (
                      <Link to={`/library`}>
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View in Library
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserSubmissions;